import { GTMNoScript, GTMScript } from "../../helpers";
import { BaseGTMRunner } from "../BaseGTM/BaseGTMRunner";

let instance: GoogleTagManagerRunner | null = null;

const FooterEvents = {
  "store locator": "store_locator_click",
};

export class GoogleTagManagerRunner extends BaseGTMRunner {
  private eventsTrackingPrepared = false;
  private scriptsPrepared = false;

  constructor() {
    super();
    /**
     * @type {number}
     */
    const currentPath = window.location.pathname.replace(".html", "");
    const cartPath = this.config.CART_CHECKOUT_PAGE_URL?.replace(".html", "");
    this.isCartPage = currentPath === cartPath;
    this.isLoyaltyPage = !!document.querySelector<HTMLElement>("[data-des-app=loyalty]");
    this.gtmContainer = document.getElementById("gtmContainer");

    document.addEventListener("DOMContentLoaded", () => {
      this.onLoadTracking = undefined;

      if (this.gtmContainer) {
        sessionStorage.removeItem("gtmInstance");
        this.onLoadTracking = this.gtmContainer.dataset.onLoadTracking;
        const code = this.gtmContainer.dataset.gtmCode;

        if (code) {
          this.prepareScripts(code);
          this.prepareEventsTracking();
        }
      }
    });
  }

  prepareEventsTracking() {
    if (this.eventsTrackingPrepared) return;

    this.currentName = this.getCurrentNameForGtm();

    if (this.currentName) {
      this.eventsTrackingPrepared = true;
      this.instance = null;
      this.getCurrentDataObject();
      this.trackClick();
      this.trackFooter();
      this.trackDynamicElements();
      this.trackCustomEvents();
      this.trackMessageBusEvents();

      setTimeout(() => {
        this.trackSingleClick();
      }, 5000);
    }
  }

  prepareScripts(code = "GTM-57WFMN3") {
    if (this.scriptsPrepared) return;

    this.scriptsPrepared = true;
    window.document.querySelector("head")?.append(GTMScript(code));
    window.document.querySelector("body")?.prepend(GTMNoScript(code));
  }

  trackClick() {
    const attribute = "data-track-click";
    Array.from(window.document.querySelectorAll(`[${attribute}]`)).forEach((element) => {
      element.addEventListener("click", ($event) => {
        const clickedElement = $event.currentTarget as HTMLElement | null;
        if (!clickedElement) {
          return;
        }
        const trackingType = clickedElement.getAttribute(attribute);
        const label = clickedElement.querySelector(".aem-button__text");

        if (!trackingType || trackingType.length === 0) {
          return;
        }

        const ctaName: string =
          (trackingType === "cta_click" && label?.textContent?.replace(/\s+/g, " ").trim()) || "cta_click";
        const trackingData = { cta_location: this.currentName, cta_name: ctaName };
        this.trackEvent(trackingType, trackingData);
      });
    });
  }

  trackFooter() {
    const links = Array.from(window.document.querySelectorAll("#footer .footer__listsRow .aem-list__link"));
    const subscribeBlock = document.querySelector(".aem-subscribe-form .aem-subscribe-form__send-btn");

    if (subscribeBlock) {
      subscribeBlock.addEventListener("click", () => {
        this.trackEvent("newsletter_subscription", undefined);
      });
    }

    if (links && links.length > 0) {
      links.forEach((link) => {
        link.addEventListener("click", () => {
          const name = link.querySelector<HTMLElement>(".aem-list__text")?.innerText.toLocaleLowerCase();
          const currentFooterEvent =
            name && name in FooterEvents ? FooterEvents[name as keyof typeof FooterEvents] : null;

          if (currentFooterEvent) {
            this.trackEvent(currentFooterEvent, undefined);
          }
        });
      });
    }
  }

  trackSingleClick() {
    const elements = [
      {
        element: [
          ...window.document.querySelectorAll<HTMLElement>("header .navigation__link"),
          ...window.document.querySelectorAll<HTMLElement>(
            ".navigation__secondLevelContent .aem-list__item > .aem-list__link"
          ),
        ],
        name: "menu_navigation",
        additionalData: (element: HTMLElement) => {
          const checkElement = element.tagName.toLocaleLowerCase() !== "a" ? element.querySelector("a") : element;

          return {
            menu_link: checkElement?.textContent?.replace(/[\n\r]+|[\s]{2,}/g, " ").trim() || "",
          };
        },
      },
      {
        element: window.document.querySelectorAll<HTMLElement>(".navigation__simpleTeaserLink"),
        name: "list_click",
        additionalData: () => undefined,
      },
      {
        element: window.document.querySelectorAll<HTMLElement>(
          ".ageconfirmation__actionWrapper .aem-button__container"
        ),
        name: "age_verification",
        additionalData: (element: HTMLElement) => {
          const elementToCheck =
            element.tagName.toLocaleLowerCase() === "span" ? element.parentNode?.parentNode : element;
          const checkElement = (elementToCheck as HTMLElement)?.classList.contains("ageconfirmation__leaveBtn");
          return {
            age_confirmation: checkElement ? "no" : "yes",
          };
        },
      },
    ];

    elements.forEach(({ element, name, additionalData }) => {
      [...element].forEach((singleElement) =>
        singleElement.addEventListener("click", () => {
          this.trackEvent(name, additionalData(singleElement) as any);
        })
      );
    });
  }

  trackDynamicElements() {
    const elements = [
      {
        name: "review_interaction",
        additionalData: () => undefined,
        check: (element: HTMLElement) =>
          element.classList.contains("trustpilotreviewswidget") ||
          element.classList.contains("aem-trustpilot-widget__review-content"),
      },
    ];

    document.addEventListener("click", (e) => {
      elements.forEach((element) => {
        if (element.check(e.target as HTMLElement)) {
          this.trackEvent(element.name, element.additionalData());
        }
      });
    });

    // Track FAQ click
    document.addEventListener("click", (event) => {
      if (this.currentName !== "faq-page") {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest(".cmp-accordion__button")) {
        this.trackEvent("faq_click", undefined);
      }
    });
  }

  static override getInstance() {
    if (!instance) {
      instance = new GoogleTagManagerRunner();
    }

    return instance;
  }
}
